import { Show } from "solid-js";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@fleet/ui";
import { useI18n } from "@/providers/i18n-provider";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { createPowerSyncQuery } from "@fieldcheck/shared/solid";
import {
  cn,
  getInspectionStatusCountsQuery,
  getOpenFindingCountsQuery,
  getTemplatesQuery,
  queryKeys,
  type TemplateRecord,
} from "@fieldcheck/shared";
import { msg } from "@lingui/core/macro";
import { BarChart3 } from "@fleet/icons";
import { COMPACT_THRESHOLD, formatCompactNumber } from "@fleet/utils";

interface StatsSectionProps {
  userId: string;
}

export function StatsSection(props: StatsSectionProps) {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const { t } = useI18n();

  const inspectionStats = createPowerSyncQuery<{ status: string | null; count: number }>({
    queryKey: () => queryKeys.inspections.stats(props.userId),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      return dbInstance ? getInspectionStatusCountsQuery(dbInstance) : null;
    },
    enabled: () => Boolean(drizzle() && props.userId),
    throttleTime: 300,
    suspense: false,
  });

  const findingStats = createPowerSyncQuery<{ severity: string | null; count: number }>({
    queryKey: () => queryKeys.findings.openStats(props.userId),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      return dbInstance ? getOpenFindingCountsQuery(dbInstance) : null;
    },
    enabled: () => Boolean(drizzle() && props.userId),
    throttleTime: 300,
    suspense: false,
  });

  const templates = createPowerSyncQuery<TemplateRecord>({
    queryKey: () => queryKeys.templates.list(props.userId),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      return dbInstance ? getTemplatesQuery(dbInstance) : null;
    },
    enabled: () => Boolean(drizzle() && props.userId),
    throttleTime: 300,
    suspense: false,
  });

  const statusCount = (status: string) =>
    (inspectionStats.data ?? []).find((r) => r.status === status)?.count ?? 0;

  const totalInspections = () => {
    let total = 0;
    for (const row of inspectionStats.data ?? []) {
      total += row.count;
    }
    return total;
  };

  const openFindings = () => {
    let total = 0;
    for (const row of findingStats.data ?? []) {
      total += row.count;
    }
    return total;
  };

  const completionPct = () => {
    const total = totalInspections();
    if (total === 0) {
      return 0;
    }
    return Math.round((statusCount("completed") / total) * 100);
  };

  return (
    <Card>
      <CardHeader>
        <div class="flex items-center gap-2">
          <BarChart3 class="h-5 w-5 text-primary" />
          <CardTitle>{t(msg`Statistics`)}</CardTitle>
        </div>
      </CardHeader>
      <CardContent class="space-y-6">
        <div class="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <StatCard
            label={t(msg`Inspections`)}
            value={totalInspections()}
            colorClass="text-primary"
            bgClass="bg-primary/10"
            isLoading={inspectionStats.isPending}
          />
          <StatCard
            label={t(msg`Completed`)}
            value={statusCount("completed")}
            colorClass="text-success"
            bgClass="bg-success/10"
            isLoading={inspectionStats.isPending}
          />
          <StatCard
            label={t(msg`Open findings`)}
            value={openFindings()}
            colorClass="text-warning"
            bgClass="bg-warning/10"
            isLoading={findingStats.isPending}
          />
          <StatCard
            label={t(msg`Templates`)}
            value={templates.data?.length ?? 0}
            colorClass="text-foreground"
            bgClass="bg-muted/50"
            isLoading={templates.isPending}
          />
        </div>

        <Show when={totalInspections() > 0 && !inspectionStats.isPending}>
          <div class="space-y-2">
            <div class="flex items-center justify-between text-xs text-muted-foreground">
              <span>{t(msg`Completion rate`)}</span>
              <span class="font-bold text-foreground">{completionPct()}%</span>
            </div>
            <div class="h-2 w-full overflow-hidden rounded-full bg-muted">
              <div
                class="h-full bg-green-500 transition-transform duration-500 origin-left"
                style={{ transform: `scaleX(${completionPct() / 100})` }}
              />
            </div>
          </div>
        </Show>
      </CardContent>
    </Card>
  );
}

function StatCard(props: {
  label: string;
  value: number;
  colorClass: string;
  bgClass: string;
  isLoading?: boolean;
}) {
  const { locale } = useI18n();
  const isCompact = () => props.value >= COMPACT_THRESHOLD;
  const formattedValue = () => formatCompactNumber(props.value, locale());
  const fullLabel = () => `${props.value.toLocaleString(locale())} ${props.label.toLowerCase()}`;

  return (
    <div
      class={cn(
        "flex flex-col items-center justify-center rounded-2xl p-4 text-center transition-all hover:scale-[1.02]",
        props.bgClass,
      )}
    >
      <Show
        when={props.isLoading}
        fallback={
          <Show
            when={isCompact()}
            fallback={
              <span
                class={cn(
                  "text-2xl font-black leading-none tracking-tight select-none",
                  props.colorClass,
                )}
                aria-label={fullLabel()}
              >
                {props.value}
              </span>
            }
          >
            <Tooltip openDelay={350} closeDelay={100}>
              <TooltipTrigger class="focus-visible:outline-none select-none cursor-help">
                <span
                  class={cn(
                    "text-2xl font-black leading-none tracking-tight border-b border-dashed border-current pb-0.5",
                    props.colorClass,
                  )}
                  aria-label={fullLabel()}
                >
                  {formattedValue()}
                </span>
              </TooltipTrigger>
              <TooltipContent class="max-w-[200px] text-center font-semibold bg-background text-foreground border border-border/40 shadow-lg px-2.5 py-1.5 rounded-lg select-none">
                {fullLabel()}
              </TooltipContent>
            </Tooltip>
          </Show>
        }
      >
        <Skeleton class="h-7 w-8" />
      </Show>
      <div class="mt-2 text-[10px] font-bold text-muted-foreground uppercase tracking-widest opacity-80 select-none">
        {props.label}
      </div>
    </div>
  );
}
